// client/src/components/AdminRoute.tsx - SECURE ADMIN ROUTE PROTECTION
import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { supabase } from '../supabase';

interface AdminRouteProps {
    children: React.ReactNode;
}

export default function AdminRoute({ children }: AdminRouteProps) {
    const [loading, setLoading] = useState(true);
    const [isAuthorized, setIsAuthorized] = useState(false);
    const [user, setUser] = useState<any>(null);

    useEffect(() => {
        checkAdminAccess();
    }, []);

    const checkAdminAccess = async () => {
        try {
            const { data: { user }, error } = await supabase.auth.getUser();

            if (error || !user) {
                setIsAuthorized(false);
                setLoading(false);
                return;
            }

            setUser(user);

            // Check admin status via secure RPC
            const { data: isAdmin } = await supabase.rpc('check_is_admin');
            setIsAuthorized(!!isAdmin);
        } catch (error) {
            console.error('Error checking admin access:', error);
            setIsAuthorized(false);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Verifying admin access...</p>
                </div>
            </div>
        );
    }

    if (!user) {
        // Not logged in - redirect to login
        return <Navigate to="/login" replace />;
    }

    if (!isAuthorized) {
        // Logged in but not authorized admin - show access denied
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-8 text-center">
                    <div className="mx-auto h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                        <svg className="h-8 w-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
                    <p className="text-gray-600 mb-6">
                        You do not have administrator privileges to access this page.
                    </p>
                    <div className="text-sm text-gray-500 mb-4">
                        Current user: {user.email}
                    </div>
                    <button
                        onClick={() => window.history.back()}
                        className="w-full py-2 px-4 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition"
                    >
                        Go Back
                    </button>
                </div>
            </div>
        );
    }

    // User is authorized admin - render the admin component
    return <>{children}</>;
}